import { getSkillLevel } from './utils';

/** Background colors offered by the badge picker, in display order. */
export const BADGE_COLORS = [
  0xcc0000, 0xff4400, 0xff9900, 0xffcc00, 0x00cc11, 0xbbcc00, 0xeeff00,
  0xffee00, 0x00cc66, 0x00bb99, 0x0099cc, 0x0055ff, 0x660099, 0x3300cc,
  0x003388, 0x0033cc, 0x9900cc, 0xcc33cc, 0xcc6699, 0xffcccc, 0xeedd99,
  0xaa7744, 0x884433, 0xdd5566, 0xeeeeee, 0xaaaaaa, 0x777777, 0x333333,
] as const;

/** Badge icon value meaning "auto skill badge" (no custom icon). */
export const SKILL_BADGE_ICON = 0;

export const ICONS_PER_ROW = 10;
const FIRST_PLAYER_ICON = 11;
const PLAYER_ICON_ROWS = 8;
const LEVEL_ICON_OFFSET = 100;
const MAX_LEVEL_ICON = 11;

/** The final row unlocks one icon per this many stickers. */
const LAST_ROW_STICKER_STEP = 10000;

/** Sticker count required to unlock each row of player icons. */
const ROW_STICKER_LIMITS = [200, 400, 800, 800, 1000, 3000, 10000] as const;

export interface BadgeIconOption {
  /** Value persisted as the player's `badgeIcon`. */
  value: number;
  /** Which movie clip renders the icon. */
  kind: 'player' | 'level';
  /** 1-based frame of the `PlayerIcon` / `LevelIcon` movie clip. */
  frame: number;
  /** Stickers required to unlock a player icon (level icons unlock by level). */
  unlockStickers?: number;
  locked: boolean;
}

export interface BadgeIconSection {
  /** Fewest stickers that unlock an icon in this section; null for level icons. */
  minStickers: number | null;
  /** Whether some icons in this section need more than `minStickers`. */
  andUp: boolean;
  options: BadgeIconOption[];
}

function getStickerLimit(row: number, col: number) {
  return ROW_STICKER_LIMITS[row] ?? LAST_ROW_STICKER_STEP * (col + 1);
}

/**
 * Icon choices for the badge picker: sticker-unlocked player icons grouped by
 * the sticker count that unlocks them, followed by the skill-level icons.
 */
export function getBadgeIconSections(stickers: number): BadgeIconSection[] {
  const level = getSkillLevel(stickers);
  const cols = Array.from({ length: ICONS_PER_ROW }, (_, col) => col);
  const sections: BadgeIconSection[] = [];

  for (let row = 0; row < PLAYER_ICON_ROWS; row++) {
    const options = cols.map((col): BadgeIconOption => {
      const value = FIRST_PLAYER_ICON + row * ICONS_PER_ROW + col;
      const unlockStickers = getStickerLimit(row, col);
      return {
        value,
        kind: 'player',
        frame: value,
        unlockStickers,
        locked: stickers < unlockStickers,
      };
    });
    const minStickers = options[0]!.unlockStickers!;
    const andUp = options.some((o) => o.unlockStickers !== minStickers);
    const previous = sections[sections.length - 1];

    // Rows starting at the same sticker count share a section
    if (previous?.minStickers === minStickers) {
      previous.options.push(...options);
      previous.andUp ||= andUp;
    } else {
      sections.push({ minStickers, andUp, options });
    }
  }

  sections.push({
    minStickers: null,
    andUp: false,
    options: cols.map(
      (col): BadgeIconOption => ({
        value: LEVEL_ICON_OFFSET + col + 1,
        kind: 'level',
        frame: col + 1,
        locked: col >= level,
      }),
    ),
  });

  return sections;
}

/**
 * A background color only applies to a custom icon, so picking a color while
 * the auto skill badge is shown switches to the player's current level icon.
 */
export function resolveIconForColorPick(icon: number, stickers: number) {
  return icon === SKILL_BADGE_ICON
    ? LEVEL_ICON_OFFSET + Math.min(MAX_LEVEL_ICON, getSkillLevel(stickers))
    : icon;
}
